/**
 * AttentionRequired Section Component
 *
 * Two-tier surface at the top of the System Status page:
 *   - Attention Required: severity error/warning (real problems).
 *   - Notes: severity info (notable but not urgent).
 *
 * Both panels are collapsed by default; the colour + count in the header give
 * the at-a-glance signal, one click reveals the table.
 */

import React, { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorIcon from '@mui/icons-material/Error'
import InfoIcon from '@mui/icons-material/Info'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { AttentionItem } from '../../../../../services/reportsApi'
import { formatDateTime } from '../../../../../utils/inspectorFormatters'

interface AttentionRequiredProps {
  items: AttentionItem[]
}

const MAX_VISIBLE_ITEMS = 50
const TABLE_MAX_HEIGHT = 240
const CELL_FONT_SIZE = '0.75rem'
const CELL_PADDING = '4px 8px'

const getSeverityIcon = (severity: string) => {
  switch (severity) {
    case 'error':
      return <ErrorIcon color="error" fontSize="small" />
    case 'warning':
      return <WarningIcon color="warning" fontSize="small" />
    default:
      return <InfoIcon color="info" fontSize="small" />
  }
}

const denseCellSx = {
  fontSize: CELL_FONT_SIZE,
  padding: CELL_PADDING,
}

const ActionButton: React.FC<{ link: string }> = ({ link }) => {
  if (link.startsWith('#')) {
    return (
      <Button
        size="small"
        variant="outlined"
        sx={{ fontSize: CELL_FONT_SIZE, py: 0.25, minWidth: 0 }}
        onClick={(e) => {
          e.stopPropagation()
          const id = link.slice(1)
          document
            .getElementById(id)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }}
      >
        View
      </Button>
    )
  }
  return (
    <Button
      component={RouterLink}
      to={link}
      size="small"
      variant="outlined"
      sx={{ fontSize: CELL_FONT_SIZE, py: 0.25, minWidth: 0 }}
      onClick={(e) => e.stopPropagation()}
    >
      View
    </Button>
  )
}

interface AttentionTableProps {
  items: AttentionItem[]
  variant: 'warning' | 'info'
}

const AttentionTable: React.FC<AttentionTableProps> = ({ items, variant }) => {
  const [expanded, setExpanded] = useState(false)
  const visibleItems = items.slice(0, MAX_VISIBLE_ITEMS)
  const overflow = items.length - visibleItems.length
  const title = variant === 'warning' ? 'Attention Required' : 'Notes'
  const icon = variant === 'warning' ? <WarningIcon /> : <InfoIcon />

  const accentColor = variant === 'warning' ? 'warning.main' : 'info.main'

  return (
    <Alert
      severity={variant}
      icon={icon}
      action={
        <IconButton
          size="small"
          aria-label={expanded ? 'collapse' : 'expand'}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      }
      sx={{
        mb: 2,
        borderLeft: 4,
        borderLeftColor: accentColor,
        boxShadow: 2,
        alignItems: 'flex-start',
        '& .MuiAlert-icon': { mt: '2px' },
        '& .MuiAlert-message': { flex: 1, width: '100%' },
        '& .MuiAlert-action': { alignItems: 'flex-start', pt: 0, mt: 0 },
      }}
    >
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          userSelect: 'none',
          mb: expanded ? 1 : 0,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Chip
          label={items.length}
          size="small"
          color={variant}
          variant="outlined"
          sx={{ height: 20, fontSize: CELL_FONT_SIZE, fontWeight: 600 }}
        />
      </Box>
      <Collapse in={expanded}>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxHeight: TABLE_MAX_HEIGHT }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width={32} sx={denseCellSx}></TableCell>
                <TableCell sx={denseCellSx}>Type</TableCell>
                <TableCell sx={denseCellSx}>FP</TableCell>
                <TableCell sx={denseCellSx}>Message</TableCell>
                <TableCell sx={denseCellSx}>Time</TableCell>
                <TableCell align="right" sx={denseCellSx}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell sx={denseCellSx}>{getSeverityIcon(item.severity)}</TableCell>
                  <TableCell sx={denseCellSx}>
                    <Chip
                      label={item.type.replace(/_/g, ' ')}
                      size="small"
                      color={
                        item.severity === 'error'
                          ? 'error'
                          : item.severity === 'warning'
                            ? 'warning'
                            : 'info'
                      }
                      variant="outlined"
                      sx={{ fontSize: CELL_FONT_SIZE, height: 20 }}
                    />
                  </TableCell>
                  <TableCell sx={denseCellSx}>
                    {item.fp_code && (
                      <Box>
                        <Typography
                          component="span"
                          sx={{ fontSize: CELL_FONT_SIZE, fontWeight: 500 }}
                        >
                          {item.fp_code.toUpperCase()}
                        </Typography>
                        {item.fp_name && (
                          <Typography
                            component="span"
                            color="text.secondary"
                            sx={{ ml: 1, fontSize: CELL_FONT_SIZE }}
                          >
                            {item.fp_name}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={denseCellSx}>{item.message}</TableCell>
                  <TableCell sx={denseCellSx}>
                    <Typography
                      color="text.secondary"
                      sx={{ fontSize: CELL_FONT_SIZE }}
                    >
                      {item.timestamp ? formatDateTime(item.timestamp) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={denseCellSx}>
                    <ActionButton link={item.link} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {overflow > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Showing {visibleItems.length} of {items.length} items
          </Typography>
        )}
      </Collapse>
    </Alert>
  )
}

const AttentionRequired: React.FC<AttentionRequiredProps> = ({ items }) => {
  if (items.length === 0) {
    return null
  }

  const urgent = items.filter(i => i.severity === 'error' || i.severity === 'warning')
  const informational = items.filter(i => i.severity === 'info')

  return (
    <>
      {urgent.length > 0 && <AttentionTable items={urgent} variant="warning" />}
      {informational.length > 0 && <AttentionTable items={informational} variant="info" />}
    </>
  )
}

export default AttentionRequired
